import React from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';

interface NoteContentProps {
  event: NostrEvent;
  className?: string;
  disableEmbeds?: boolean;
  hideEmbedImages?: boolean;
  disableNoteEmbeds?: boolean;
  disableMediaEmbeds?: boolean;
  onImageClick?: (url: string) => void;
}

// Minimal but effective NoteContent implementation that extracts links, mentions, hashtag and renders inline.
export function NoteContent({ event, className = "", disableEmbeds = false, disableMediaEmbeds = false, disableNoteEmbeds = false, onImageClick }: NoteContentProps) {
  const content = event.content || '';
  
  // Quick emoji detection
  const isEmojiOnly = content.length < 10 && /^[\p{Emoji}\s]+$/u.test(content) && content.replace(/\s/g, '').length > 0;

  // Regular expressions
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const hashtagRegex = /#([\p{L}\p{N}_]+)/gu;
  const mentionRegex = /(nostr:)?((npub|note|nevent|naddr|nprofile)1[a-z0-9]+)/gi;

  // Render tokens
  const parts = content.split(/(\s+)/);

  const renderWord = (word: string, index: number) => {
    // Check for image urls
    if (urlRegex.test(word)) {
      urlRegex.lastIndex = 0; // reset
      const isImage = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(word);
      const isVideo = /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(word);

      if (isImage && !disableMediaEmbeds && !disableEmbeds) {
        return (
          <div key={index} className="my-2 max-w-full overflow-hidden rounded-2xl border border-border cursor-pointer">
            <img
              src={word}
              alt="Attached Media"
              className="max-h-96 w-full object-cover"
              loading="lazy"
              onClick={(e) => {
                e.stopPropagation();
                onImageClick?.(word);
              }}
            />
          </div>
        );
      }

      if (isVideo && !disableMediaEmbeds && !disableEmbeds) {
        return (
          <video key={index} src={word} controls className="my-2 max-h-96 w-full rounded-2xl border border-border object-cover" />
        );
      }

      return (
        <a 
          key={index} 
          href={word} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-sky-500 hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {word}
        </a>
      );
    }

    // Check for hashtags
    if (word.startsWith('#')) {
      hashtagRegex.lastIndex = 0;
      const match = hashtagRegex.exec(word);
      if (match) {
        const tag = match[1];
        return (
          <Link 
            key={index} 
            to={`/explore?q=${encodeURIComponent('#' + tag)}`} 
            className="text-sky-500 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {word}
          </Link>
        );
      }
    }

    // Check for Nostr Mentions
    if (mentionRegex.test(word)) {
      mentionRegex.lastIndex = 0;
      const match = mentionRegex.exec(word);
      if (match) {
        const id = match[2];
        return (
          <Link 
            key={index} 
            to={`/${id}`} 
            className="text-sky-500 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            @{id.slice(0, 10)}...
          </Link>
        );
      }
    }

    return <span key={index}>{word}</span>;
  };

  return (
    <div className={`whitespace-pre-wrap break-words leading-relaxed text-[15px] ${isEmojiOnly ? 'text-4xl' : ''} ${className}`}>
      {parts.map((part, index) => {
        if (/^\s+$/.test(part)) {
          return <span key={index}>{part}</span>;
        }
        return renderWord(part, index);
      })}
    </div>
  );
}
